<!--
	@name app-firm-tab-view-panels
	@description Firm Panels
	@date 2020/08/26
	@license no license
	@copywrite Answers In Retirement Limited
-->

<template>
	<div :component="$options.name">
		<common-structure-section>
			<template #body>
				<v-fade-transition hide-on-leave>
					<div v-if="!panelList">
						<p class="text-body-1 mb-3 text-center">
							Loading panels
						</p>
						<v-progress-linear height="6" indeterminate />
					</div>
				</v-fade-transition>
				<v-fade-transition hide-on-leave>
					<div v-if="panelList">
						<v-row no-gutters class="justify-space-between pb-5">
							<v-col class="align-self-left">
								<v-pagination v-model="currentPage" :length="pageCount" :total-visible="8" color="info" />
							</v-col>
							<v-col class="shrink align-self-center">
								<v-btn
									v-if="department.name.includes('owner') || department.name.includes('manager')"
									:loading="editLoading[`add`]"
									@click="openProvidersDialog(null)"
								>
									Add New Panel
								</v-btn>
							</v-col>
						</v-row>
						<div v-if="!panelList.length">
							<div class="text-body-1 font-weight-bold mb-3">
								Create separate Panels with which to group Accounts.
							</div>
							<div class="text-body-2 mb-3">
								Once a Panel has been created, you can assign it to an Account using the Link Panel action in the Manage Accounts section. Assigning an account to a
								Panel will result in the Panel being used in place of the Company details for system generated documentation such as Fact Finds.
							</div>
							<div class="text-body-2">
								Click the ADD NEW PANEL button to get started.
							</div>
						</div>
						<v-data-table
							v-else
							class="table--styled elevation-3"
							:footer-props="footerProps"
							:headers="headers"
							:items="orderedPanels"
							:items-per-page="itemsPerPage"
							:page.sync="currentPage"
							@page-count="pageCount = $event"
						>
							<template #body="{ items }">
								<tbody>
									<tr v-for="item in items" :key="item.id" class="tbl-row">
										<td>
											<p class="text-body-1 font-weight-bold mb-1 mr-2">
												{{ item.name }}
											</p>
											<v-chip v-if="item.isDefault && !isOrganisationTradingStyleActive" x-small label color="yellow darken-3">
												Preferred
											</v-chip>
										</td>
										<td class="shrunk">
											{{ item.created | moment('Do MMM YYYY, HH:mm') }}
										</td>
										<td v-if="department.name.includes('owner') || department.name.includes('manager')" class="text-right shrunk">
											<v-tooltip v-if="!isOrganisationTradingStyleActive" bottom content-class="text-center" max-width="250">
												<template #activator="{ on }">
													<v-btn class="mr-2" :color="item.isDefault ? 'yellow darken-3' : 'grey'" icon @click="submitSetDefaultPanel(item)" v-on="on">
														<v-icon>{{ item.isDefault ? 'mdi-star' : 'mdi-star-outline' }}</v-icon>
													</v-btn>
												</template>
												<span>{{ item.isDefault ? 'Remove as preferred' : 'Set as preferred' }}</span>
											</v-tooltip>
											<v-tooltip bottom content-class="text-center" max-width="250">
												<template #activator="{ on }">
													<v-btn color="primary" icon :loading="editLoading[item.id]" @click="openProvidersDialog(item)" v-on="on">
														<v-icon>mdi-pencil</v-icon>
													</v-btn>
												</template>
												<span> Edit panel </span>
											</v-tooltip>
											<v-tooltip bottom content-class="text-center" max-width="250">
												<template #activator="{ on }">
													<v-btn color="error" icon class="ml-2" :loading="deleteLoading[item.id]" @click="submitDeletePanel(item)" v-on="on">
														<v-icon>mdi-trash-can</v-icon>
													</v-btn>
												</template>
												<span> Delete this panel </span>
											</v-tooltip>
										</td>
									</tr>
								</tbody>
							</template>
						</v-data-table>
					</div>
				</v-fade-transition>
			</template>
		</common-structure-section>

		<common-dialog-confirm ref="confirm" />
		<app-firm-dialog-confirm-default ref="confirmDefault" />
		<app-firm-dialog-add-providers ref="providersDialog" :organisations="organisations" @submitted="loadPanelList" @panel-opened="panelOpened" />
		<app-firm-dialog-restriction ref="restrictionDialog" @submitted="loadPanelList" />
	</div>
</template>

<script>
	import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
	import { orderBy } from 'lodash';
	import { ElementTools } from '@/utils';

	import CommonStructureSection from '@/component/common/structure/section';
	import CommonDialogConfirm from '@/component/common/dialog/confirm';
	import AppFirmDialogConfirmDefault from '../dialog/confirm-default';
	import AppFirmDialogAddProviders from '../dialog/add-providers';
	import AppFirmDialogRestriction from '../dialog/restriction';

	export default {
		name: 'app-firm-members-list',

		components: { CommonStructureSection, CommonDialogConfirm, AppFirmDialogConfirmDefault, AppFirmDialogAddProviders, AppFirmDialogRestriction },

		props: {},

		data() {
			return {
				ots: [],
				editLoading: {},
				deleteLoading: {},
				formData: {},
				currentPage: 1,
				itemsPerPage: 10,
				pageCount: 0,
				matterTypes: [],
				organisations: [],
				selectedProviders: [],
				organisationUserList: [],
				footerProps: {
					'items-per-page-options': [5, 10, 15, 20, 25],
					showFirstLastPage: true,
					firstIcon: 'mdi-arrow-collapse-left',
					lastIcon: 'mdi-arrow-collapse-right',
					prevIcon: 'mdi-minus',
					nextIcon: 'mdi-plus'
				}
			};
		},

		computed: {
			...mapState('CmsSite', ['site']),
			...mapState('Account', ['user', 'organisation', 'department']),
			...mapState('AccountPanel', ['panelList']),
			...mapGetters('CmsForm', ['form']),
			...mapGetters('AccountOrganisationUser', ['userTradingStyle']),
			...mapGetters('Account', ['isOrganisationTradingStyleActive']),

			headers() {
				return [
					{ text: 'Name', align: 'left', value: 'name', class: 'text-no-wrap', sortable: false },
					{ text: 'Created', align: 'left', value: 'created', class: 'text-no-wrap', sortable: false },
					{ text: '', align: 'centre', sortable: false }
				];
			},

			orderedPanels() {
				return orderBy(this.panelList, 'name', ['asc']) || [];
			}
		},

		created() {
			this.loadPanelList();
			this.fetchOrganisationUserList();
			this.loadForm('firmPanel');
		},

		methods: {
			...mapActions('AccountOrganisationUser', ['activateUserOrganisationTradingStyle']),
			...mapActions('AccountPanel', ['loadPanelList', 'deletePanel', 'modifyPanel']),
			...mapActions('AccountOrganisationUser', ['loadOrganisationUserList']),
			...mapActions('CmsForm', ['loadForm']),
			...mapActions('AccountUser', ['modifyUser']),
			...mapMutations('Account', ['setUser']),
			...mapMutations('AccountOrganisationUser', ['setTradingStyle']),
			...mapMutations('AccountPanel', ['setPanel']),

			fetchOrganisationUserList() {
				this.loadOrganisationUserList(this.organisation.id).then(({ data }) => {
					this.organisationUserList = data;
				});
			},

			/**
			 * @name openProvidersDialog
			 * @description Event for provider panel dialog
			 */
			openProvidersDialog(item) {
				this.$set(this.editLoading, item?.id ?? `add`, true);

				const userPanel = this.user.panel; // Have we got a set panel for this user?
				const userTradingStyle = this.userTradingStyle?.organisationTradingStyleId; // Have we got a set trading style for this user?

				if (userPanel || userTradingStyle) {
					let message = `In order to add/edit a panel, it is necessary to change your account's panel status to "Whole of Market". OK to proceed?`;
					let button = `Reset Panel`;
					let success = `Your panel has been set to "Whole of Market"`;
					let failure = `There was an error trying to reset your panel`;
					if (userTradingStyle) {
						message = `In order to add/edit a panel, it is necessary to remove your active trading style. OK to proceed?`;
						button = `Reset Trading Style`;
						success = `Your trading style has been removed`;
						failure = `There was an error trying to remove your trading style`;
					}

					this.$refs.confirm
						.open(button, message, { persistent: true })
						.then(async() => {
							let promises = [];
							// Remove panel
							if (userPanel)
								promises.push(
									this.modifyUser({
										id: this.user.id,
										payload: {
											data: {
												panel: null
											}
										}
									})
								);
							// Remove trading style
							if (userTradingStyle)
								promises.push(
									this.activateUserOrganisationTradingStyle({
										userId: this.user.id,
										organisationTradingStyleId: null,
										name: null,
										fca: null
									})
								);

							Promise.all(promises)
								.then(async() => {
									if (userPanel) await this.setUser({ panel: null });
									ElementTools.fireNotification(this.$el, 'success', success);
									this.$refs.providersDialog.open(item);
								})
								.catch((e) => {
									console.log(e);
									ElementTools.fireNotification(this.$el, 'error', failure);
								});
						})
						.finally(() => this.$set(this.editLoading, item?.id ?? `add`, false));
				} else {
					this.$refs.providersDialog.open(item);
				}
			},

			panelOpened(item) {
				this.$set(this.editLoading, item?.id ?? `add`, false);
			},

			/**
			 * @name openRestrictionDialog
			 * @description Event for product restriction dialog
			 */
			openRestrictionDialog(item, id) {
				this.$refs.restrictionDialog.open(item, id);
			},

			/**
			 * @name submitSetDefaultPanel
			 * @description Set or unset panel as default
			 * @param {Object} item
			 */
			async submitSetDefaultPanel(item) {
				const isSettingDefault = !item.isDefault;
				const action = isSettingDefault ? 'set as preferred' : 'remove as preferred';
				const existingDefault = this.panelList.find((panel) => panel.isDefault && panel.id !== item.id);

				let message = '';

				if (isSettingDefault) {
					message = `<p class="mb-0">Set "${item.name}" as your preferred panel?</p>`;

					if (existingDefault)
						message += `<p class="mt-2 mb-0">Note: only one panel can be preferred at any one time. Clicking OK will remove the preferred status from "${existingDefault.name}".</p>`;
				} else {
					message = `<p class="mb-0">Are you sure you wish to remove ${item.name} as your preferred panel?</p>`;
				}

				this.$refs.confirmDefault
					.open(isSettingDefault ? 'Set Preferred Panel' : 'Remove Preferred Panel', null, message)
					.then(async() => {
						this.$refs.confirmDefault.setLoading(true);

						this.modifyPanel({ id: item.id, payload: { isDefault: isSettingDefault } })
							.then(() => {
								if (isSettingDefault && existingDefault) this.setPanel({ data: { ...existingDefault, isDefault: false }, id: existingDefault.id });

								ElementTools.fireNotification(
									this.$el,
									'success',
									`${item.name} has been successfully ${isSettingDefault ? 'set as preferred' : 'removed as preferred'}`
								);
							})
							.catch(() => ElementTools.fireNotification(this.$el, 'error', `Failed to ${action} panel`))
							.finally(() => {
								this.loadPanelList();
								this.$refs.confirmDefault.setLoading(false);
								this.$refs.confirmDefault.close();
							});
					})
					.catch(() => {});
			},

			/**
			 * @name submitDeletePanel
			 * @description Delete panel
			 * @param {Object} item
			 */
			async submitDeletePanel(item) {
				this.$set(this.deleteLoading, item.id, true);

				if (this.userByPanelId(item.id))
					ElementTools.fireNotification(
						this.$el,
						'error',
						`${item.name} cannot be removed since there are accounts associated to it. Please unlink all accounts from this panel before attempting to delete it`
					);
				else {
					this.$refs.confirm
						.open('Delete Panel', `Are you sure you wish to delete ${item.name}?`, { persistent: true })
						.then(async() => {
							this.deletePanel(item.id)
								.then(() => ElementTools.fireNotification(this.$el, 'success', `${item.name} has been successfully deleted`))
								.catch(() => ElementTools.fireNotification(this.$el, 'error', 'Failed to delete panel'));
						})
						.finally(() => this.$set(this.deleteLoading, item.id, false));
				}
			},

			/**
			 * @name userByPanelId
			 * @description Get user by organisation panel id
			 * @param {Object} item
			 */
			userByPanelId(id) {
				return this.organisationUserList.find((u) => u.panel && u.panel.id === id);
			}
		}
	};
</script>

<style lang="scss" scoped>
	::v-deep .v-pagination {
		justify-content: start !important;
	}
</style>
